import { InMemoryCache } from '@apollo/client';
import {
  createFragmentRegistry,
  NormalizedCacheObject
} from '@apollo/client/cache';
import { ApolloLink } from '@apollo/client/core';
import { ApolloClient } from '@apollo/client/core/ApolloClient';
import { setContext } from '@apollo/client/link/context';
import { createHttpLink } from '@apollo/client/link/http/createHttpLink';
import { RetryLink } from '@apollo/client/link/retry';

import { userCookies } from '@/shared/constants/constants';
import * as gatewayFragments from '@/shared/graphql/gateway/fragments';
import { queryCountryConfigs } from '@/shared/graphql/gateway/queries';
import { initStorefrontApolloClient } from '@/shared/utils/storefront-client';

import { useUserStore } from '../pinia/modules/user';
import { getToken } from './get-token';
import { hobbiiLocalStorage } from './web-storage';

const recoveryLink = (onSuccess: (token: string) => void) =>
  new RetryLink({
    delay: {
      initial: 0
    },
    attempts: {
      max: 2,
      retryIf: async (error) => {
        if (error.statusCode !== 401) {
          return false;
        }

        // Nothing is cleared when this fails. The token endpoint comes back
        // empty both for a buyer with no session and for a signed-in one
        // whose logged_in_customer_id Shopify dropped, and the two are
        // indistinguishable here — so clearing would sign the second buyer
        // out for good, the stored pass being the only thing boot can restore
        // a session from. A real sign-out is reconciled at boot, where
        // Shopify is the one saying so.
        const token = await getToken(true);
        if (token) {
          onSuccess(token);
        }
        return !!token;
      }
    }
  });

const enrichLink = () =>
  setContext(async (_, { headers }) => {
    // Must read the same store the user module initialises from. That store is
    // populated synchronously at page load, whereas the boot sequence fills in
    // anything else asynchronously — so reading elsewhere leaves a window where
    // the page considers the customer signed in but sends no pass, and the
    // request is refused before the retry link recovers it.
    const token = hobbiiLocalStorage.getItem(userCookies.C_ID);

    const authTokenHeader = token ? { Authorization: `Bearer ${token}` } : {};

    return {
      headers: {
        ...headers,
        ...authTokenHeader,
        'Content-Type': 'application/json'
      }
    };
  });

const httpLink = (graphqlHost: string) =>
  createHttpLink({
    uri: graphqlHost
  });

const cache = new InMemoryCache({
  typePolicies: {
    CommunityQueries: {
      keyFields: ['__typename']
    },
    CrafterQueries: {
      keyFields: ['__typename']
    },
    // The points screen reads the balance and the history through two separate
    // queries on this same container. Without a key it stays un-normalised and
    // the second write replaces the first instead of merging with it, dropping
    // whichever subtree was cached first.
    LoyaltyQueries: {
      keyFields: ['__typename']
    }
  },
  fragments: createFragmentRegistry(...Object.values(gatewayFragments))
});

export type ApolloClientInitOptions = {
  graphqlHost: string;
};

export const apolloClientInit = ({ graphqlHost }: ApolloClientInitOptions) => {
  const userStore = useUserStore();

  const setReceivedCustomer = (token: string) => {
    userStore.setCustomer(token);
  };

  return new ApolloClient({
    link: ApolloLink.from([
      recoveryLink(setReceivedCustomer),
      enrichLink(),
      httpLink(graphqlHost)
    ]),
    cache
  });
};

// degraded: true means the client carries no real token (construction
// failed) — still safe to use structurally (clientId: 'storefront'
// resolution keeps working), but any query against it will fail normally.
type StorefrontClientResult = {
  client: ApolloClient<NormalizedCacheObject>;
  degraded: boolean;
};

export const getStorefrontApolloClient = async (
  gatewayApolloClient: ApolloClient<NormalizedCacheObject>
): Promise<StorefrontClientResult> => {
  const shopifyDomain = `https://${window.Shopify.shop}`;
  const storeName = window.Shopify.shop.split('.')[0]; // TODO: any smarter way of getting the storeName?

  try {
    const countryConfigsResponse = await gatewayApolloClient.query(
      queryCountryConfigs()
    );

    const publicAccessToken =
      countryConfigsResponse.data?.countryConfigs?.find(
        (config) => storeName === config?.shopifyConfig?.storefront?.name
      )?.shopifyConfig?.storefront?.publicAccessToken ?? '';

    if (!publicAccessToken) {
      throw new Error(
        `No publicAccessToken received from Gateway for storeName: "${storeName}".`
      );
    }

    return {
      client: initStorefrontApolloClient(shopifyDomain, publicAccessToken),
      degraded: false
    };
  } catch (error) {
    // Callers on the render path must never be blocked on this — return a
    // client anyway so clientId: 'storefront' resolution keeps working.
    // Queries against it fail normally (bad/empty token) and surface
    // through each caller's existing Apollo error state instead of
    // rejecting here. getSharedStorefrontClient below still treats this as
    // a failure for caching purposes.
    console.error(error);
    return {
      client: initStorefrontApolloClient(shopifyDomain, ''),
      degraded: true
    };
  }
};

export const getGatewayApolloClient = () =>
  apolloClientInit({
    graphqlHost: import.meta.env.VITE_HOBBII_GATEWAY
  });

export const getApolloClients = async () => {
  const gatewayApolloClient = getGatewayApolloClient();

  const { client: storefrontApolloClient, degraded: storefrontDegraded } =
    await getStorefrontApolloClient(gatewayApolloClient);

  return {
    gateway: gatewayApolloClient,
    storefront: storefrontApolloClient,
    storefrontDegraded
  };
};

let sharedStorefrontClientPromise: Promise<
  ApolloClient<NormalizedCacheObject>
> | null = null;

// Every call re-fetches the storefront token via a gateway round-trip.
// Callers that may resolve many items concurrently (e.g. carousel slide
// fallback resolution) should share one client/token fetch instead of
// triggering one gateway call per item.
export const getSharedStorefrontClient = () => {
  if (!sharedStorefrontClientPromise) {
    sharedStorefrontClientPromise = getStorefrontApolloClient(
      getGatewayApolloClient()
    ).then(({ client, degraded }) => {
      if (degraded) {
        // Don't cache a degraded client — a transient failure would
        // otherwise permanently poison every later resolution attempt on
        // the page. Reject so existing retry logic in callers still fires.
        sharedStorefrontClientPromise = null;
        throw new Error('Storefront client unavailable');
      }
      return client;
    });
  }
  return sharedStorefrontClientPromise;
};
